import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { MemberProfileDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../../components/layout/AdminLayout';
import { useMemberApi } from '../../../hooks/useMemberApi';
import MemberProfileForm from '../../../components/forms/MemberProfileForm';
import { useUserApi } from '../../../hooks/useUserApi';
import { useScrollToHash } from '../../../hooks/useScrollToHash';

const MemberProfileEdit: React.FC = () => {
  const [member, setMember] = useState<MemberProfileDTO>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getProfile, editProfile } = useMemberApi();
  const { uploadProfilePicture } = useUserApi();

  // Scroll to section based on URL hash
  useScrollToHash({ isLoading });


  const breadcrumbs = [
    { label: 'Member', href: '#' },
    { label: 'Profile', href: '/admin/profile' },
    { label: 'Edit', href: '#' },
  ];

  useEffect(() => {
    const fetchMember = async (): Promise<void> => {
      try {
        const response = await getProfile();
        if (response && response.success && response.data) {
          setMember(response.data);
        }
      } catch (error) {
        console.error('Error fetching member:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMember();
  }, []);

   const handleSubmit = async (data: MemberProfileDTO): Promise<void> => {
     setIsSubmitting(true);
     try {
       if (profilePicture) {
         const uploadResponse = await uploadProfilePicture(
           profilePicture.name,
           profilePicture,
         );

         if (uploadResponse && uploadResponse.success) {
           data.profilePicture = uploadResponse.data;
         }
       }
       
       const response = await editProfile(data);
       if (response && response.success && response.data) {
         // Invalidate profile picture cache to force refresh
         queryClient.invalidateQueries({ queryKey: ['profilePictureSas', response.data.userId] });
         navigate('/admin/profile');
       }
     } catch (error) {
       console.error('Error updating member:', error);
     } finally {
       setIsSubmitting(false);
     }
   };

  return (
    <AdminLayout
      activeMenuItem="/admin/member-profile"
      breadcrumbs={breadcrumbs}
      allowedRoles={['User']}
      requiredPermission='Manage.MemberProfile.Edit'
    >
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <MemberProfileForm
          initialValues={member!}
          isSubmitting={isSubmitting}
          onSave={handleSubmit}
          setProfilePicture={setProfilePicture}
        />
      )}
    </AdminLayout>
  );
};

export default MemberProfileEdit;
